"use strict";
/**
 * Unit conversions for the values on a waste characterization.
 *
 * These live here, in code, rather than in an extraction prompt. A model does
 * `lbs/gal ÷ 8.34` unreliably and code does it exactly, and a conversion buried
 * in prose cannot be tested or reused. The extractors ask for the number and the
 * unit the sheet printed; this turns that into what the profile stores.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.WATER_DENSITY = exports.DensityUnit = exports.TemperatureUnit = void 0;
exports.toFahrenheit = toFahrenheit;
exports.toCelsius = toCelsius;
exports.toSpecificGravity = toSpecificGravity;
exports.specificGravityFromRange = specificGravityFromRange;
/** The temperature scales a sheet states a flash point in. */
exports.TemperatureUnit = {
    FAHRENHEIT: "FAHRENHEIT",
    CELSIUS: "CELSIUS",
};
/**
 * The ways a sheet reports how heavy the waste is.
 *
 * Specific gravity is dimensionless and relative to water; the rest are real
 * densities that have to be divided by water's value in the same units. Kept as a
 * closed set so the extractor has something definite to choose from and an
 * unrecognized unit cannot silently become a specific gravity.
 */
exports.DensityUnit = {
    SPECIFIC_GRAVITY: "SPECIFIC_GRAVITY",
    GRAMS_PER_ML: "GRAMS_PER_ML",
    GRAMS_PER_CM3: "GRAMS_PER_CM3",
    KILOGRAMS_PER_LITER: "KILOGRAMS_PER_LITER",
    POUNDS_PER_GALLON: "POUNDS_PER_GALLON",
};
/**
 * Water's density in each unit, which is what makes a reading dimensionless.
 *
 * The metric three are 1 by definition at the temperatures these sheets care
 * about. `SPECIFIC_GRAVITY` is 1 because such a reading is already dimensionless
 * — listing it here rather than special-casing it keeps the conversion one
 * division with no branch.
 */
exports.WATER_DENSITY = {
    [exports.DensityUnit.SPECIFIC_GRAVITY]: 1,
    [exports.DensityUnit.GRAMS_PER_ML]: 1,
    [exports.DensityUnit.GRAMS_PER_CM3]: 1,
    [exports.DensityUnit.KILOGRAMS_PER_LITER]: 1,
    [exports.DensityUnit.POUNDS_PER_GALLON]: 8.34,
};
/** Convert a temperature to Fahrenheit, which is what `flashPointF` holds. */
function toFahrenheit(value, unit) {
    return unit === exports.TemperatureUnit.CELSIUS ? value * (9 / 5) + 32 : value;
}
function toCelsius(value, unit) {
    return unit === exports.TemperatureUnit.FAHRENHEIT ? (value - 32) * (5 / 9) : value;
}
/**
 * Convert a density to a dimensionless specific gravity.
 *
 * Returns null for a non-finite reading rather than propagating a NaN into a
 * Float column, and for a non-positive one: a density of zero or less is not a
 * measurement, and dividing by water would give a value that looks real.
 */
function toSpecificGravity(value, unit) {
    if (!Number.isFinite(value) || value <= 0)
        return null;
    return value / exports.WATER_DENSITY[unit];
}
/**
 * Collapse a reported density range to the single specific gravity the profile
 * stores.
 *
 * The midpoint, and deliberately not the low or high end: neither is more
 * conservative for a value that feeds a weight calculation in both directions.
 * The range the sheet stated is preserved on the extraction payload, so the
 * collapse is visible to a reviewer rather than silent — which is the part that
 * was wrong when the prompt did this.
 */
function specificGravityFromRange(low, high, unit) {
    const lowSg = low == null ? null : toSpecificGravity(low, unit);
    const highSg = high == null ? null : toSpecificGravity(high, unit);
    if (lowSg != null && highSg != null)
        return (lowSg + highSg) / 2;
    return lowSg ?? highSg;
}
//# sourceMappingURL=characterizationConversions.js.map